/**
 * Canonical error normalization for the ARGOS frontend.
 *
 * Every form / mutation in the app gets back a `NormalizedError` from
 * `extractError(err)` regardless of where the error came from (axios
 * network failure, FastAPI 422 with Pydantic field errors, FastAPI 4xx
 * with `{detail: "..."}`, 5xx, 429 with `retry_after_seconds`, etc).
 *
 * Why this exists: every form used to reinvent its own error parsing
 * (FeedbackModal had ~25 lines of `if err.response.status === ...`,
 * AddWellModal had its own variant, FieldRecordForm had a third). The
 * messages were sparse ("Something went wrong") and the field-level
 * errors that FastAPI returns on 422 were silently dropped. This helper
 * makes "premium error UX" a one-line opt-in for every form.
 */

import type { AxiosError } from 'axios';

export type ErrorCode =
  | 'rate_limited'   // 429 — retry after a delay
  | 'maintenance'    // 503 — endpoint disabled
  | 'validation'     // 422 — field errors (Pydantic)
  | 'forbidden'      // 401/403 — not authorised
  | 'not_found'      // 404
  | 'conflict'       // 409 — e.g. duplicate name
  | 'server'         // 5xx
  | 'network'        // axios failed to reach the server
  | 'unknown';

export interface NormalizedError {
  /** Human-facing message. Always populated, never empty. */
  message: string;
  /** HTTP status, when known. */
  status?: number;
  /** Stable, programmatic code for switch statements. */
  code: ErrorCode;
  /**
   * Per-field errors, keyed by field name (the last segment of FastAPI's
   * ``loc`` array). Populated on 422 responses. Empty for non-validation
   * errors.
   */
  fieldErrors: Record<string, string>;
  /** Whether the operation is safe to retry without changing inputs. */
  retryable: boolean;
  /** When `code === 'rate_limited'`, when the next attempt is allowed. */
  retryAfterSeconds?: number;
}

interface FastApiValidationItem {
  loc: (string | number)[];
  msg: string;
  type?: string;
}

interface MaybeFastApi {
  detail?: string | { msg?: string } | FastApiValidationItem[] | unknown;
  retry_after_seconds?: number;
}


/** Has axios populated a response object? (network errors haven't.) */
function isAxiosLike(err: unknown): err is AxiosError {
  if (typeof err !== 'object' || err === null) return false;
  return 'isAxiosError' in err || 'response' in err || 'request' in err;
}

function asString(v: unknown): string | undefined {
  if (typeof v === 'string' && v.trim()) return v.trim();
  return undefined;
}

function lastLoc(loc: (string | number)[]): string {
  // Skip the leading 'body' / 'query' / 'path' segment FastAPI prepends.
  const tail = loc.length > 1 ? loc.slice(1) : loc;
  return String(tail[tail.length - 1] ?? 'form');
}


/**
 * Normalize whatever the caller threw / awaited into a `NormalizedError`.
 *
 * Always returns a populated object — never throws, never returns null.
 */
export function extractError(err: unknown): NormalizedError {
  // 1. Plain string / Error objects (defensive — most callers throw axios)
  if (typeof err === 'string') {
    return {
      message: err,
      code: 'unknown',
      fieldErrors: {},
      retryable: true,
    };
  }
  if (err instanceof Error && !isAxiosLike(err)) {
    return {
      message: err.message || 'Unexpected error.',
      code: 'unknown',
      fieldErrors: {},
      retryable: true,
    };
  }

  // 2. Axios error with NO response = network failure
  if (isAxiosLike(err) && !err.response) {
    return {
      message: 'Network error — check your connection and try again.',
      code: 'network',
      fieldErrors: {},
      retryable: true,
    };
  }

  // 3. Axios error WITH response — interrogate
  if (isAxiosLike(err) && err.response) {
    const status = err.response.status;
    const data = err.response.data as MaybeFastApi | undefined;
    const detail = data?.detail;

    // 422 → Pydantic field errors
    if (status === 422 && Array.isArray(detail)) {
      const fieldErrors: Record<string, string> = {};
      for (const item of detail as FastApiValidationItem[]) {
        if (item && Array.isArray(item.loc) && item.msg) {
          fieldErrors[lastLoc(item.loc)] = item.msg;
        }
      }
      return {
        message:
          Object.keys(fieldErrors).length > 0
            ? 'Please fix the highlighted fields.'
            : 'The server rejected this request.',
        status,
        code: 'validation',
        fieldErrors,
        retryable: false,
      };
    }

    // 429 → rate limited
    if (status === 429) {
      const retryAfter = Number(data?.retry_after_seconds) || undefined;
      const minutes = retryAfter ? Math.max(1, Math.round(retryAfter / 60)) : null;
      return {
        message: minutes
          ? `You've hit the rate limit — try again in ${minutes}m.`
          : "You've hit the rate limit. Try again in a few minutes.",
        status,
        code: 'rate_limited',
        fieldErrors: {},
        retryable: true,
        retryAfterSeconds: retryAfter,
      };
    }

    // 503 → maintenance / disabled
    if (status === 503) {
      return {
        message:
          asString(detail) ??
          'This is temporarily disabled. Please try again later.',
        status,
        code: 'maintenance',
        fieldErrors: {},
        retryable: false,
      };
    }

    // 401 / 403 → permission
    if (status === 401 || status === 403) {
      return {
        message:
          asString(detail) ??
          (status === 401
            ? "You're signed out. Sign back in to continue."
            : "You don't have permission to do that."),
        status,
        code: 'forbidden',
        fieldErrors: {},
        retryable: false,
      };
    }

    // 404
    if (status === 404) {
      return {
        message: asString(detail) ?? "We couldn't find that item.",
        status,
        code: 'not_found',
        fieldErrors: {},
        retryable: false,
      };
    }

    // 409
    if (status === 409) {
      return {
        message: asString(detail) ?? 'Conflict — that already exists.',
        status,
        code: 'conflict',
        fieldErrors: {},
        retryable: false,
      };
    }

    // 4xx with a string detail
    if (status >= 400 && status < 500) {
      return {
        message:
          asString(detail) ??
          asString((detail as { msg?: string } | undefined)?.msg) ??
          `Request rejected (${status}).`,
        status,
        code: 'unknown',
        fieldErrors: {},
        retryable: false,
      };
    }

    // 5xx
    if (status >= 500) {
      return {
        message:
          'Our server hit an issue. Try again — if it keeps happening, send us feedback.',
        status,
        code: 'server',
        fieldErrors: {},
        retryable: true,
      };
    }
  }

  return {
    message:
      'Something unexpected happened. Try again, or refresh the page if it keeps failing.',
    code: 'unknown',
    fieldErrors: {},
    retryable: true,
  };
}
